import type { Screen } from "./state";

/**
 * The top-right icon row (16.98, Don's direction).
 *
 * Lived inline in the Expedition View, which meant the Station and Base Views
 * had no Options, no Handbook, no Save and no Galaxy Map -- a captain who docked
 * had to undock to change the volume or read the glossary. Extracted here so all
 * three top-level Views share one definition and one set of rules about what
 * belongs where.
 *
 * The row is deliberately NOT uniform. Two icons are station services and two
 * are always-available, and encoding that here rather than per-screen is the
 * point of the module: a nav row that offers a door which cannot open is worse
 * than one that omits it.
 */

export type NavContext = "expedition" | "station" | "base";

/**
 * The captain's situation, as far as the nav row cares (16.99, Don's rulings).
 *
 * Context alone was not enough. Two of the game's real states leave a screen
 * with nothing to operate on regardless of where the ship is parked, and both
 * were still offering the icon:
 *
 * - **In the escape pod** there is no ship at all, so Cargo, Fleet and Market
 *   are all doors onto an empty room.
 * - **Flying a Loaner Ship** forbids managing cargo outright (16.63) -- the
 *   Cargo screen already opens on a paragraph explaining that, which is the same
 *   "door onto a refusal" 16.98 removed the Market icon for.
 */
export interface NavSituation {
  inEscapePod?: boolean;
  isLoanerShip?: boolean;
}

export interface NavIcon {
  screen: Screen | "saveExit";
  glyph: string;
  title: string;
  /** Where this icon appears. Absent means everywhere. */
  contexts?: NavContext[];
  /** Situations that remove this icon wherever it would otherwise appear. */
  hiddenWhen?: (s: NavSituation) => boolean;
  className?: string;
}

/**
 * Don's ruling: you cannot take a contract or trade goods from open space.
 *
 * Both were reachable from the Expedition View, and both were misleading rather
 * than merely generous -- the Market's own header already said "Dock at a
 * Station to trade", so the icon led to a screen whose first line was a refusal.
 * The Mission Board was worse: it worked, so missions could be accepted from
 * anywhere, which quietly removed the reason to ever dock.
 *
 * They are Station-only rather than "anywhere you are docked". A Player-Owned
 * Base is your own yard, not a market or a hiring hall -- the Market screen
 * cannot transact without a Station anyway (16.92's `hasStation` gate), so a
 * Base icon would lead to the same refusal the Expedition View one did.
 */
const STATION_ONLY: NavContext[] = ["station"];

export const NAV_ICONS: NavIcon[] = [
  // The Bridge is a career record, not a ship system -- it survives losing the
  // ship, which is arguably when a captain most wants to look at it.
  { screen: "lounge", glyph: "🎖️", title: "Bridge — your skills and career record" },
  // Cargo stays available in flight (Don): what is in the hold decides what you
  // do next out there, and since 16.98 it also names the best market for it.
  {
    screen: "cargo",
    glyph: "📦",
    title: "Cargo — your hold, and where each item sells best",
    hiddenWhen: (s) => !!s.inEscapePod || !!s.isLoanerShip
  },
  { screen: "fleet", glyph: "🚀", title: "Fleet Roster", hiddenWhen: (s) => !!s.inEscapePod },
  {
    screen: "market",
    glyph: "💰",
    title: "Market — buy and sell",
    contexts: STATION_ONLY,
    hiddenWhen: (s) => !!s.inEscapePod
  },
  // 16.100: hidden in the pod too, per Don. Accepting a contract you have no
  // hull to run is the same door-onto-a-refusal as the rest of this list.
  {
    screen: "board",
    glyph: "📋",
    title: "Mission Board — accept contracts",
    contexts: STATION_ONLY,
    hiddenWhen: (s) => !!s.inEscapePod
  },
  { screen: "diary", glyph: "📖", title: "Mission Diary" },
  { screen: "spaceMap", glyph: "🗺️", title: "Galaxy Map" },
  { screen: "options", glyph: "⚙️", title: "Options" },
  {
    screen: "faq",
    glyph: "?",
    title: "Captain's Handbook — FAQ, glossary, ships and items",
    className: "xv-nav-help"
  },
  // 16.99: Don ruled this stays available even mid-fight, on the condition that
  // leaving mid-fight is survivable -- so the title now promises what
  // combatSnapshot delivers rather than leaving the captain to guess.
  {
    screen: "saveExit",
    glyph: "💾",
    title: "Save and return to the Main Menu — a fight in progress is saved and resumes where you left it"
  }
];

export function iconsFor(context: NavContext, situation: NavSituation = {}): NavIcon[] {
  return NAV_ICONS.filter(
    (i) => (!i.contexts || i.contexts.includes(context)) && !(i.hiddenWhen?.(situation) ?? false)
  );
}

/**
 * The row's markup. `inline` drops the absolute positioning for the Station and
 * Base Views, which are ordinary document-flow screens rather than the
 * Expedition View's full-viewport HUD.
 */
export function navIconsHtml(context: NavContext, inline = false, situation: NavSituation = {}): string {
  const buttons = iconsFor(context, situation)
    .map((i) => {
      const attr = i.screen === "saveExit" ? "data-save-exit" : `data-nav-icon="${i.screen}"`;
      return `<button type="button" class="xv-nav-icon ${i.className ?? ""}" ${attr} title="${i.title}">${i.glyph}</button>`;
    })
    .join("\n      ");
  return `<div class="xv-nav-icons ${inline ? "xv-nav-icons-inline" : ""}">
      ${buttons}
    </div>`;
}

/**
 * Wires the row. Kept next to the markup so a new icon cannot be added in one
 * place and left dead in the other.
 */
export function wireNavIcons(
  scope: HTMLElement,
  onNavigate: (screen: Screen) => void,
  onSaveExit: () => void
): void {
  scope.querySelectorAll<HTMLButtonElement>("[data-nav-icon]").forEach((btn) => {
    btn.addEventListener("click", () => onNavigate(btn.dataset.navIcon as Screen));
  });
  scope.querySelector<HTMLButtonElement>("[data-save-exit]")?.addEventListener("click", onSaveExit);
}
